import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { observer } from "mobx-react";
import ClassesApi from "@api/classes.api";
import useCurrentProject from "@hooks/useCurrentProject";

import FormControlLabel from "@mui/material/FormControlLabel";
import { IconButton, Button } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";

import MainContentHeader from "@components/MainContentHeader";
import Wrapper from "@components/Wrapper";
import Loader from "@components/Loader";
import Icon from "@components/Icon";

import store from "./store";
import stores from "@stores";

import * as commonS from "@styles/common";
import * as S from "./styles";
import projectsApi from "../../api/projects.api";

const KlassenScreen = () => {
  const { currentProject, refresh: refreshProject } = useCurrentProject();
  const { classes, isLoading, errors } = store;
  const navigate = useNavigate();
  const { dictionariesStore } = stores;
  const { projectInstitutions } = dictionariesStore;
  const {
    layoutStore: { setConfirmModal }
  } = stores;
  const [classesArr, setClassesArr] = useState([]);
  const [isNew, setNew] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [newBtnDisabled, setNewBtnDisabled] = useState(false);

  const fetch = async () => {
    if (currentProject?._id) {
      await dictionariesStore.getInstitutionsListByProject(currentProject?._id);
      await store.getClassesList(currentProject?._id);
    }
  };

  const handleSave = async () => {
    const lastChild = classesArr.length - 1;
    if (isNew) {
      await ClassesApi.add({
        ...classesArr[lastChild],
        institution: classesArr[lastChild].institution._id,
        project: currentProject?._id,
      }).then((e: any) =>
        toast.success('Klasse erfolgreich hinzugefügt', { autoClose: 3000 })
      );
      await store.getClassesList(currentProject?._id);
      setDisabled(true);
      setNew(false);
    } else {
      const currObj = classesArr.filter((e) => !classes.includes(e));
      for (let i = 0; i < currObj.length; i++) {
        await ClassesApi.edit({ ...currObj[i] });
      }
      await store.getClassesList(currentProject?._id);
      setDisabled(true);
    }
  };

  const handleAddNew = async (id: string) => {
    if (classesArr.length && isNew) {
      handleSave();
    }
    const arr = [...classesArr];
    setNew(true);
    arr.push({
      institution: {
        _id: id,
      },
    });
    setClassesArr(arr);
    if (arr.length === classes.length + 1) {
      setNewBtnDisabled(true);
    }
  };

  const handleDelete = async (id: string, index: number) => {
    if (classes[index]) {
      setConfirmModal({
        type: "warning",
        open: true,
        title: "Element wirklich löschen?",
        positiveCallback: async () => {
          await ClassesApi.delete(id).then((e: any) =>
            toast.success('Klasse erfolgreich gelöscht', { autoClose: 3000 })
          );
          await store.getClassesList(currentProject?._id);
          setNewBtnDisabled(false);
        },
        positiveButtonText: "Ok",
        negativeButtonText: "Cancel",
      });
    } else {
      const arr = [...classesArr];
      arr.splice(index, 1);
      setClassesArr(arr);
      setNewBtnDisabled(false);
    }
  };

  useEffect(() => {
    if (currentProject) {
      fetch();
    }
  }, [currentProject?.type]);

  useEffect(() => {
    if (classes) {
      setClassesArr(classes);
    }
  }, [classes]);

  useEffect(() => {
    if (errors) {
      toast.error(errors?.message)
    }
  }, [errors]);

  const handleChange = (
    index: number,
    name: string,
    value: string | number | boolean
  ) => {
    if (classes[index]) {
      setNew(false);
    } else {
      setNew(true);
    }
    setDisabled(false);
    let currObj = [...classesArr][index];
    currObj = {
      ...currObj,
      [name]: value.toString(),
    };
    const arr = [...classesArr];
    arr[index] = currObj;

    if (!currObj.name || !currObj.teacherName) {
      setDisabled(true);
    } else if (
      (arr.length === classes.length + 1 && currObj.name) ||
      currObj.teacherName
    ) {
      setNewBtnDisabled(false);
    }
    setClassesArr(arr);
  };

  const setInstitutionColor = async (color, institutionId) => {
    await projectsApi.edit({
      _id: currentProject._id,
      colorMapping: {
        ...currentProject.colorMapping,
        [institutionId]: color,
      },
    });

    await refreshProject();
  };

  const getInstitutionColor = (institutionId) => {
    const { colorMapping } = currentProject;
    if (colorMapping && colorMapping[institutionId]) {
      return colorMapping[institutionId];
    } else {
      return "#ffffff";
    }
  };

  const renderRow = (item: any, index: number) => (
    <S.FormWrap className={classes[index] ? "saved" : "non-saved"}>
      <S.Actions>
        <IconButton onClick={() => handleDelete(item._id, index)}>
          <Icon icon="trash" size={15} color="#E92B2B" />
        </IconButton>
      </S.Actions>
      <S.Row>
        <S.FormItem>
          <InputLabel htmlFor="name" shrink={false}>
            Name Klasse
          </InputLabel>
          <TextField
            id="name"
            defaultValue={item ? item?.name : ""}
            name="name"
            error={!item?.name}
            onChange={(e) => handleChange(index, e.target.name, e.target.value)}
            variant="outlined"
          />
        </S.FormItem>
        <S.FormItem>
          <InputLabel htmlFor="teacherName" shrink={false}>
            Name Lehrperson
          </InputLabel>
          <TextField
            id="teacherName"
            defaultValue={item ? item?.teacherName : ""}
            onChange={(e) => handleChange(index, e.target.name, e.target.value)}
            error={!item?.teacherName}
            name="teacherName"
            variant="outlined"
          />
        </S.FormItem>
      </S.Row>
      <S.Row>
        <S.FormItem>
          <S.FiledContainer>
            <Typography>Anzahl SuS:</Typography>
            <S.Field>
              <InputLabel htmlFor="minStudentsNumber" shrink={false}>
                Minimal:
              </InputLabel>
              <TextField
                id="minStudentsNumber"
                defaultValue={item ? item?.minStudentsNumber : 1}
                onChange={(e) =>
                  handleChange(index, e.target.name, e.target.value)
                }
                type="number"
                name="minStudentsNumber"
                variant="outlined"
              />
            </S.Field>
            <S.Field>
              <InputLabel htmlFor="maxStudentsNumber" shrink={false}>
                Maximum:
              </InputLabel>
              <TextField
                id="maxStudentsNumber"
                defaultValue={item ? item?.maxStudentsNumber : 1}
                onChange={(e) =>
                  handleChange(index, e.target.name, e.target.value)
                }
                type="number"
                name="maxStudentsNumber"
                variant="outlined"
              />
            </S.Field>
          </S.FiledContainer>
        </S.FormItem>
      </S.Row>
    </S.FormWrap>
  );

  return (
    <commonS.MainBody>
      {currentProject && (
        <>
          <MainContentHeader
            title={currentProject.title}
            buttonTitle="Zu Projekten"
            onButtonClick={() => navigate("/projekte")}
          />
          <Wrapper
            headTitle="Klassen:"
            disabled={disabled}
            onButtonClick={() => handleSave()}
          >
            {projectInstitutions.map((item: any, index: number) => (
              <S.AccordionWrap key={item._id} defaultExpanded={index === 0}>
                <S.AccordionTitle
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  expandIcon={
                    <Icon icon="triagle-bottom" size={10} color="#778097" />
                  }
                >
                  <Typography>
                    <strong className="title">
                      <span>{index + 1}. </span>
                      {item.address}
                    </strong>
                  </Typography>
                </S.AccordionTitle>
                <S.AccordionDetailsWrap>
                  <S.FormItem style={{ margin: "0 15px 15px" }}>
                    <input
                      type="color"
                      id="color"
                      name="color"
                      onChange={(e) => {
                        setInstitutionColor(e.target.value, item._id);
                      }}
                      defaultValue={getInstitutionColor(item._id)}
                    />
                  </S.FormItem>
                  {classesArr.map((e, index) => (
                    <div key={index}>
                      {e.institution._id === item._id && renderRow(e, index)}
                    </div>
                  ))}
                  <Button
                    className="addButton"
                    disabled={newBtnDisabled}
                    onClick={() => handleAddNew(item._id)}
                  >
                    + Neue Klasse
                  </Button>
                </S.AccordionDetailsWrap>
              </S.AccordionWrap>
            ))}
            {isLoading && <Loader filled={false} />}
          </Wrapper>
        </>
      )}
    </commonS.MainBody>
  );
};

export default observer(KlassenScreen);
